import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../config/axios';
import { useCart } from '../contexts/CartContext';
import { formatPickupWindow } from '../utils/dateTimeUtils';
import { useTranslation } from '../hooks/useTranslation';

interface Order {
  id: string;
  pickup_date_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  total_amount: number;
  status: string;
  is_paid: boolean;
  created_at: string;
  paid_at: string | null;
  items: {
    id: string;
    product_id: string;
    product_name_en: string;
    product_name_zh: string;
    quantity_kg: number;
    price_per_kg: number;
    subtotal: number;
  }[];
  pickup_date: {
    id: string;
    pickup_date: string;
    pickup_start_time: string;
    pickup_end_time: string;
    location_name: string;
    location_address: string;
  };
}

const PaymentReturnPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'pending'>('pending');
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError(t('paymentReturn.noSessionId'));
      setLoading(false);
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId: string) => {
    try {
      console.log('🔍 Checking payment status for session:', sessionId);
      
      // Check Stripe session status
      const statusResponse = await axiosInstance.get(`/api/stripe/session-status?session_id=${sessionId}`);
      console.log('✅ Session status response:', statusResponse.data);
      
      const { status, payment_status } = statusResponse.data;

      if (status === 'complete' && payment_status === 'paid') {
        // Payment successful - fetch order details
        const orderIds = JSON.parse(sessionStorage.getItem('pendingOrderIds') || '[]');
        
        if (orderIds.length > 0) {
          // Fetch order details
          const orderPromises = orderIds.map((orderId: string) =>
            axiosInstance.get(`/api/orders/${orderId}`)
          );
          
          const orderResponses = await Promise.all(orderPromises);
          const fetchedOrders = orderResponses.map(res => res.data);
          
          setOrders(fetchedOrders);
          setPaymentStatus('success');
          
          // Clear cart and session storage
          clearCart();
          sessionStorage.removeItem('customerInfo');
          sessionStorage.removeItem('termsAccepted');
          sessionStorage.removeItem('pendingOrderIds');
        } else {
          setPaymentStatus('success');
        }
      } else if (status === 'open') {
        // Payment not completed - could remount checkout here
        setPaymentStatus('failed');
        setError(t('paymentReturn.paymentNotCompleted'));
      } else {
        setPaymentStatus('failed');
        setError(t('paymentReturn.paymentFailed'));
      }
    } catch (err: any) {
      console.error('❌ Payment status check failed:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      setError(err.response?.data?.detail || 'Failed to verify payment status');
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>{t('paymentReturn.verifying')}</h2>
        <p>{t('paymentReturn.verifyingMessage')}</p>
      </div>
    );
  }

  if (paymentStatus === 'failed' || error) {
    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '48px', color: '#dc3545', marginBottom: '10px' }}>✗</div>
          <h1 style={{ color: '#dc3545' }}>{t('paymentReturn.failure')}</h1>
          <p style={{ fontSize: '18px', color: '#666' }}>
            {error || t('paymentReturn.failureMessage')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/cart')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            {t('paymentReturn.returnToCart')}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            {t('paymentReturn.returnToHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '10px' }}>✓</div>
        <h1 style={{ color: '#28a745' }}>{t('paymentReturn.success')}</h1>
        <p style={{ fontSize: '18px', color: '#666' }}>
          {t('paymentReturn.successMessage', { 
            count: orders.length > 1 ? t('paymentReturn.successMessagePlural') : t('paymentReturn.successMessageSingle')
          })}
        </p>
      </div>

      {orders.map((order, index) => (
        <div key={order.id} style={{ marginBottom: '30px' }}>
          <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
            <h2>{t('paymentReturn.orderDetails', { number: (index + 1).toString() })}</h2>
            <p><strong>{t('paymentReturn.orderId')}:</strong> {order.id}</p>
            <p><strong>{t('paymentReturn.orderDate')}:</strong> {new Date(order.created_at).toLocaleString()}</p>
            <p><strong>{t('paymentReturn.totalAmount')}:</strong> €{order.total_amount.toFixed(2)}</p>
            <p><strong>{t('paymentReturn.status')}:</strong> {order.status}</p>
          </div>

          <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
            <h3>{t('paymentReturn.pickupInformation')}</h3>
            <p><strong>{t('paymentReturn.dateTime')}:</strong><br />
              {formatPickupWindow(order.pickup_date.pickup_date, order.pickup_date.pickup_start_time, order.pickup_date.pickup_end_time, t('common.from'), t('common.to'))}
            </p>
            <p><strong>{t('paymentReturn.location')}:</strong><br />
              {order.pickup_date.location_name}
            </p>
            <p style={{ color: '#666' }}>
              {order.pickup_date.location_address}
            </p>
          </div>

          <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
            <h3>{t('paymentReturn.itemsOrdered')}</h3>
            {order.items.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #ddd' }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{language === 'zh' ? item.product_name_zh : item.product_name_en}</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {item.quantity_kg} kg × €{item.price_per_kg.toFixed(2)}/kg
                  </div>
                </div>
                <div style={{ fontWeight: 'bold' }}>
                  €{item.subtotal.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {orders.length > 0 && (
        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
          <h2>{t('paymentReturn.customerInformation')}</h2>
          <p><strong>{t('paymentReturn.name')}:</strong> {orders[0].customer_name}</p>
          <p><strong>{t('paymentReturn.email')}:</strong> {orders[0].customer_email}</p>
          <p><strong>{t('paymentReturn.phone')}:</strong> {orders[0].customer_phone}</p>
          <p><strong>{t('paymentReturn.address')}:</strong><br />{orders[0].customer_address}</p>
        </div>
      )}

      <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107', marginBottom: '20px' }}>
        <p style={{ margin: 0, fontSize: '14px' }}>
          <strong>{t('paymentReturn.importantTitle')}</strong> {t('paymentReturn.importantText')}
        </p>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={() => navigate('/products')}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            cursor: 'pointer',
          }}
        >
          {t('paymentReturn.placeAnotherOrder')}
        </button>
        <button
          onClick={() => navigate('/')}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            cursor: 'pointer',
          }}
        >
          {t('paymentReturn.returnToHome')}
        </button>
      </div>
    </div>
  );
};

export default PaymentReturnPage;
